import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Save, Copy } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAccount, usePartnerAccess } from '../contexts/AccountContext';
import syncService from '../services/syncService';
import syncTemplatesService from '../services/syncTemplatesService';
import hotelService from '../services/hotelService';
import accountService from '../services/accountService';
import partnerSyncTemplatesService from '../services/partnerSyncTemplatesService';
import pricingService from '../services/pricingService';
import syncPaymentService from '../services/syncPaymentService';
import WizardPaymentStep from './WizardPaymentStep';
import WizardHotelStep from './WizardHotelStep';
import SyncTemplateGraphic from './SyncTemplateGraphic';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import Card from './shared/Card';
import CopyConversionsModal from './shared/CopyConversionsModal';
import NotificationsPanel from './shared/NotificationsPanel';
import './SyncProjectFormPage.css';
import './PaymentPorterPage.css'; // pp-toggle-btn switch styles (STYLE.md canonical toggle)

// Whether this project's data comes from the SyncConnect desktop client rather than a direct connection.
// Shared by the create wizard and the edit/copy form so the two cannot drift — it drives real behaviour
// (syncprojects.uses_sync_connect, the Source-box generate/download button, and the connection path
// diagram on Configure Source), so it must read identically wherever it is set.
//
// Defaults to disabled: most projects do not use it, and turning it on offers a client build the property
// has to install. Opting in should be a deliberate act.
const SyncConnectToggle = ({ value, onChange }) => {
  const on = value === 'Y';
  return (
    <div className="form-group full-width">
      <label className="form-label">Source Client</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <button
          type="button"
          className={`pp-toggle-btn ${on ? 'pp-toggle-btn-on' : 'pp-toggle-btn-off'}`}
          onClick={() => onChange(on ? 'N' : 'Y')}
          title={on ? 'Click to disable' : 'Click to enable'}
        >
          <span className="pp-toggle-track"><span className="pp-toggle-thumb" /></span>
          <span className="pp-toggle-label">{on ? 'Enabled' : 'Disabled'}</span>
        </button>
        <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
          Uses the SyncConnect desktop client as the data source
        </span>
      </div>
    </div>
  );
};

const STEPS = ['Hotel', 'Project Info', 'Project Details', 'Conversions', 'Notifications', 'Payment'];

// Adapter: presents syncPaymentService in the shape PaymentModal expects.
// resolve() merges the sync payment data with free-seat counts from pricingService.
const buildSyncPaymentService = (hotelId, accountId) => ({
  resolve: async () => {
    const [payRes, seatsRes] = await Promise.all([
      syncPaymentService.resolve(hotelId, accountId),
      pricingService.getFreeSeatCount(hotelId, 'sync'),
    ]);
    const hotelSeats = seatsRes.data?.hotelSeats ?? 0;
    const chainSeats = seatsRes.data?.chainSeats ?? 0;
    return {
      data: {
        prices: payRes.data?.prices || [],
        paymentMethods: payRes.data?.paymentMethods || [],
        billingContacts: payRes.data?.billingContacts || null,
        upfrontPrice: payRes.data?.upfrontPrice || null,
        freeSeats: hotelSeats + chainSeats,
        freeSeatLevel: hotelSeats > 0 ? 'hotel' : (chainSeats > 0 ? 'chain' : null),
      },
    };
  },
  createSetupIntent: (acctId, acctName, hId) => syncPaymentService.createSetupIntent(acctId, acctName, hId),
  // createSubscription intentionally omitted — sync creates the subscription after project creation
});

// ─── Main wizard ──────────────────────────────────────────────────────────────
const SyncProjectFormPage = ({ mode = 'create' }) => {
  const navigate = useNavigate();
  const { syncId, hotelid } = useParams();
  const { user, isSuperAdmin, isPartner, solePartnerId } = useAuth();
  const { selectedAccount, selectedPartner, isChainAdmin } = useAccount();
  // FROM A BOOKING (Chain Settings › Scheduling › Create sync project, V597): ?scheduleRequest=<id>. The wizard is
  // prefilled from the committed booking and the cutover date is the committed one; the server links the project
  // back to the booking and holds it to that date.
  const [searchParams] = useSearchParams();
  const bookingId = mode === 'create' ? (searchParams.get('scheduleRequest') || null) : null;
  const [booking, setBooking] = useState(null);
  const { stripeReady, partnerDataLoaded } = usePartnerAccess(isSuperAdmin);
  const invoicePrivilege = (isSuperAdmin || isPartner) ? 'partner' : isChainAdmin ? 'chain' : 'hotel';

  // Wizard step (0=hotel, 1=info, 2=details, 3=conversions, 4=notifications, 5=payment)
  const initialStep = (mode === 'create' && !hotelid) ? 0 : 1;
  const [step, setStep] = useState(initialStep);
  // Tracks the project uuid once created, so a retry after a failed payment doesn't try to
  // re-create the project and trip the DUPLICATE_NAME guard on the create endpoint.
  const [createdSyncId, setCreatedSyncId] = useState(null);
  // After invoice creation, the wizard stays on the payment step showing an "awaiting payment"
  // banner instead of navigating away — the project is paused until Stripe confirms via webhook.
  const [awaitingPayment, setAwaitingPayment] = useState(false);
  const [invoiceResult, setInvoiceResult]     = useState(null); // { hostedInvoiceUrl, emailSentTo }

  // Notifications step (shared NotificationsPanel, wizard mode) — persisted on create.
  const [notif, setNotif] = useState({ emails: [], teamsWebhook: null });

  const [selectedHotel, setSelectedHotel] = useState(null);

  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Resolution per template id → { level, name }, resolved against the selected hotel.
  // Used to show the "Customised for this chain/hotel" badge on every template card.
  const [resolutions, setResolutions] = useState({});

  // Conversions step state
  const [showCopyConversionsModal, setShowCopyConversionsModal] = useState(false);
  const [conversionSource, setConversionSource] = useState(null); // { syncuuid, taskId, projectName }

  const [formData, setFormData] = useState({
    projectName: '',
    templateUuid: '',
    hotelName: '',
    propertyId: '',
    usesSyncConnect: 'N',
    projectDetails: {
      projectContactName: '',
      projectContactEmail: '',
      projectContactPhone: '',
      projectCutoverDate: '',
    },
  });

  // For edit/copy modes
  const effectiveHotelId = hotelid || selectedHotel?.hotelid;

  useEffect(() => {
    // Create-mode hotel selection is handled by the shared WizardHotelStep (self-fetching, partner-scoped).
    if (hotelid) {
      hotelService.getHotelById(hotelid)
        .then(r => {
          const h = r?.hotel || r;
          setSelectedHotel({ hotelid: h.hotelid, hotelname: h.hotelname, accountid: h.accountid });
        })
        .catch(() => {});
    }
    loadTemplates();
    if (mode === 'edit' && syncId) loadProjectData();
    if (mode === 'copy' && syncId) loadProjectData(true);
  }, []); // eslint-disable-line

  // The booking, once the chain is known (the navbar context can arrive after the first render).
  useEffect(() => {
    if (!(bookingId && selectedAccount) || booking) return;
    accountService.getSchedule(selectedAccount)
      .then((sched) => {
        const b = (sched?.requests || []).find(r => String(r.id) === String(bookingId));
        if (!b) { setError('That booking was not found for the selected chain.'); return; }
        if (b.status !== 'COMMITTED') { setError('A sync project is made from a committed booking — the partner has not committed to this one yet.'); return; }
        setBooking(b);
        setFormData(prev => ({
          ...prev,
          projectName: prev.projectName || `${b.hotelName || b.hotelCode} migration`,
          hotelName: prev.hotelName || b.hotelName || '',
          projectDetails: {
            ...prev.projectDetails,
            projectContactName: b.contactName || '',
            projectContactEmail: b.contactEmail || '',
            projectContactPhone: b.contactPhone || '',
            projectCutoverDate: b.committedDate || b.proposedDate || '',
          },
        }));
        if (b.hotelId) {
          hotelService.getHotelById(b.hotelId)
            .then(r => {
              const h = r?.hotel || r;
              setSelectedHotel({ hotelid: h.hotelid, hotelname: h.hotelname, accountid: h.accountid });
            })
            .catch(() => {});
        }
      })
      .catch(() => setError('Could not load the booking.'));
  }, [bookingId, selectedAccount]); // eslint-disable-line

  // Every create goes through here, so a project started from a booking always says which one.
  const createProject = (payload) => syncService.createSyncProject(bookingId ? { ...payload, scheduleRequestId: Number(bookingId) } : payload);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const data = await syncTemplatesService.listTemplates();
      const active = (data || []).filter(t => t.status === 'ACTIVE');
      setTemplates(active);
      if (active.length === 0) setError('No templates available. Please contact support.');
    } catch (err) {
      setError(`Failed to load templates: ${err.message || 'Please try again.'}`);
    } finally {
      setLoading(false);
    }
  };

  const loadProjectData = async (isCopy = false) => {
    try {
      setLoading(true);
      const data = await syncService.getSyncProjectDetails(syncId);
      if (data.projectDetails && Object.keys(data.projectDetails).length > 0) {
        const project = data.projectDetails;
        // The backend returns the actual project name as `projectName` and the template name
        // separately as `templateName`. Earlier code mistakenly used `templateName` here, leaving
        // the editable Project Name field showing the template's name instead of the project's.
        const baseName = project.projectName || '';
        setFormData({
          projectName:  isCopy ? (baseName ? `${baseName} (Copy)` : '') : baseName,
          templateUuid: project.templateUuid || project.syncTemplateId || '',
          hotelName:    project.hotelName  || '',
          propertyId:   project.propertyId || '',
          usesSyncConnect: project.usesSyncConnect === 'Y' ? 'Y' : 'N',
          projectDetails: {
            projectContactName: project.contactName || '',
            projectContactEmail: project.contactEmail || '',
            projectContactPhone: project.contactPhone || '',
            projectCutoverDate: project.projectCutoverDate
              ? new Date(project.projectCutoverDate).toISOString().split('T')[0] : '',
          },
        });
      }
    } catch (err) {
      setError('Failed to load project data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const effectivePartnerId = selectedPartner || solePartnerId;
    if (!effectivePartnerId || !effectiveHotelId || templates.length === 0) {
      setResolutions({});
      return;
    }
    let cancelled = false;
    Promise.all(templates.map(t =>
      partnerSyncTemplatesService.resolve(effectivePartnerId, effectiveHotelId, t.id)
        .then(result => ({ id: t.id, level: result.level, name: result.name || null }))
        .catch(() => ({ id: t.id, level: null, name: null }))
    )).then(results => {
      if (cancelled) return;
      const map = {};
      results.forEach(r => { map[r.id] = { level: r.level, name: r.name }; });
      setResolutions(map);
    });
    return () => { cancelled = true; };
  }, [effectiveHotelId, templates, user, selectedPartner]); // eslint-disable-line

  const handleConversionSourceSelected = (sourceProject) => {
    setConversionSource({
      syncuuid: sourceProject.syncuuid,
      taskId: sourceProject.taskId,
      projectName: sourceProject.projectName,
    });
    setShowCopyConversionsModal(false);
  };

  const copyConversionsFromSource = async (newProjectUuid) => {
    if (!conversionSource?.taskId) return;
    try {
      const details = await syncService.getSyncProjectDetails(newProjectUuid);
      const targetTask = (details.projectTasks || []).find(t => t.taskName === 'CONVERSIONS');
      if (!targetTask) return;
      const csvBlob = await syncService.downloadConversionsCSV(conversionSource.syncuuid, conversionSource.taskId);
      await syncService.uploadConversionsCSV(newProjectUuid, targetTask.syncProjectTaskUuid, csvBlob);
    } catch (err) {
      console.error('Failed to copy conversions:', err);
    }
  };

  const handleInput = (section, field, value) => {
    if (section === 'root') setFormData(p => ({ ...p, [field]: value }));
    else setFormData(p => ({ ...p, [section]: { ...p[section], [field]: value } }));
  };

  const canAdvance = () => {
    if (step === 0) return !!selectedHotel;
    if (step === 1) return !!formData.projectName.trim() && !!formData.templateUuid;
    if (step === 2) return !!formData.projectDetails.projectCutoverDate;
    return true;
  };

  const next = () => { if (canAdvance()) setStep(s => s + 1); };
  const back = () => setStep(s => s - 1);

  // Edit / copy mode — simple submit (no wizard, no payment)
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!formData.projectName.trim() || !formData.templateUuid || !formData.projectDetails.projectCutoverDate) {
      setError('Please fill in all required fields.');
      return;
    }
    try {
      setSaving(true);
      setError(null);
      const payload = {
        projectName:    formData.projectName,
        templateUuid:   formData.templateUuid,
        hotelName:      formData.hotelName  || null,
        propertyId:     formData.propertyId || null,
        usesSyncConnect: formData.usesSyncConnect,
        projectDetails: formData.projectDetails,
      };
      if (mode === 'edit') {
        await syncService.updateProject(syncId, payload);
        navigate(`/sync/${syncId}`);
      } else {
        const result = await createProject({ ...payload, hotelId: hotelid ? parseInt(hotelid) : undefined });
        const syncId = result?.syncId;
        navigate(syncId ? `/sync/${syncId}` : '/sync');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save project.');
    } finally {
      setSaving(false);
    }
  };

  // Payment step completion — create project then handle Stripe if needed
  const handlePaymentComplete = async (result) => {
    // Re-entry guard — without this, a duplicate onComplete (e.g. user clicks twice, or modal
    // re-fires) would POST /sync a second time and fail with DUPLICATE_NAME.
    if (saving) return;
    setSaving(true);
    setError(null);
    const hotel = selectedHotel;
    const hid = hotelid ? parseInt(hotelid) : (hotel ? parseInt(hotel.hotelid) : undefined);
    try {
      // If a previous attempt already created the project but the payment step failed, reuse the
      // existing uuid so the retry only runs the payment side and avoids the DUPLICATE_NAME error.
      let syncProjectId = createdSyncId;
      if (!syncProjectId) {
        const payload = {
          projectName: formData.projectName,
          templateUuid: formData.templateUuid,
          usesSyncConnect: formData.usesSyncConnect,
          projectDetails: formData.projectDetails,
          hotelId: hid,
          accountId: hotel?.accountid || selectedAccount,
          paid: result.type === 'free-seat' ? 'Y' : 'N',
          usedFreeSeat: result.type === 'free-seat',
          notificationEmails: notif.emails.map(({ email, source }) => ({ email, source })),
          notificationTeamsWebhook: notif.teamsWebhook || null,
        };

        const createResult = await createProject(payload);
        syncProjectId = createResult?.syncId;
        if (syncProjectId) {
          setCreatedSyncId(syncProjectId);
          await copyConversionsFromSource(createResult?.syncuuid);
        }
      }

      if (result.type === 'subscription' && syncProjectId) {
        try {
          await syncPaymentService.subscribe({
            syncProjectId,
            accountId: hotel?.accountid || selectedAccount,
            hotelId: hid,
            paymentMethodId: result.paymentMethodId,
            stripePriceId: result.stripePriceId,
          });
        } catch (stripeErr) {
          console.error('Stripe subscription failed:', stripeErr.message);
          // Project is created; payment can be retried from project page
        }
      }

      if (result.type === 'invoice' && syncProjectId) {
        try {
          const invRes = await syncPaymentService.createInvoice({
            syncProjectId,
            accountId: hotel?.accountid || selectedAccount,
            hotelId: hid,
            stripePriceId: result.stripePriceId,
            targetType: result.invoiceTarget,
            customerEmail: result.invoiceTarget === 'me' ? result.contactEmail : undefined,
          });
          setInvoiceResult(invRes?.data || null);
        } catch (invErr) {
          const msg = invErr.response?.data?.error?.message || invErr.response?.data?.message || invErr.message || 'Invoice creation failed';
          console.error('Invoice creation failed:', msg);
          setError(`Invoice creation failed: ${msg}`);
          setSaving(false);
          return;
        }
      }

      // Invoice flow: stay on the payment step showing the awaiting-payment state instead of
      // navigating away. The project is paused until Stripe confirms via webhook.
      if (result.type === 'invoice') {
        setAwaitingPayment(true);
        setSaving(false);
        return;
      }

      navigate(syncProjectId ? `/sync/${syncProjectId}` : '/sync');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create project.');
      setSaving(false);
    }
  };

  // Superadmin override — bypass Stripe and activate the sync project as paid. Creates the project
  // paid if it doesn't exist yet, or marks an already-created (invoice) project paid.
  const handleMarkPaid = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    const hotel = selectedHotel;
    const hid = hotelid ? parseInt(hotelid) : (hotel ? parseInt(hotel.hotelid) : undefined);
    try {
      let syncProjectId = createdSyncId;
      if (!syncProjectId) {
        const payload = {
          projectName: formData.projectName,
          templateUuid: formData.templateUuid,
          usesSyncConnect: formData.usesSyncConnect,
          projectDetails: formData.projectDetails,
          hotelId: hid,
          accountId: hotel?.accountid || selectedAccount,
          paid: 'Y',
          usedFreeSeat: false,
          notificationEmails: notif.emails.map(({ email, source }) => ({ email, source })),
          notificationTeamsWebhook: notif.teamsWebhook || null,
        };
        const createResult = await createProject(payload);
        syncProjectId = createResult?.syncId;
        if (syncProjectId) {
          setCreatedSyncId(syncProjectId);
          await copyConversionsFromSource(createResult?.syncuuid);
        }
      } else {
        await syncPaymentService.markInvoicePaid(syncProjectId);
      }
      navigate(syncProjectId ? `/sync/${syncProjectId}` : '/sync');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to mark paid.');
      setSaving(false);
    }
  };

  const isWizardMode = mode === 'create';
  const hotelForPayment = selectedHotel || (hotelid ? { hotelid: parseInt(hotelid), hotelname: '', accountid: selectedAccount } : null);

  if (mode !== 'create') {
    // Edit / copy — simple form without wizard wrapper
    if (loading) return (
      <div className="sync-project-form-page">
        <div className="form-container"><div className="loading-container"><Spinner size="lg" /><p className="loading-text">Loading...</p></div></div>
      </div>
    );
    return (
      <div className="sync-project-form-page">
        <div className="form-container">
          <div className="form-header">
            <div className="header-left">
              <Button variant="secondary" onClick={() => navigate(syncId ? `/sync/${syncId}` : '/sync')} className="back-button"><ArrowLeft className="icon" />Back</Button>
              <h1 className="form-title">{mode === 'edit' ? 'Edit Sync Project' : 'Copy Sync Project'}</h1>
            </div>
          </div>
          {error && <div className="error-message">{error}</div>}
          <form onSubmit={handleEditSubmit}>
            <Card className="form-card">
              <h2 className="card-title">Project Information</h2>
              <div className="form-group full-width">
                <label className="form-label required">Project Name</label>
                <input type="text" className="form-input" value={formData.projectName} onChange={e => handleInput('root', 'projectName', e.target.value)} required />
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Hotel Name</label>
                  <input type="text" className="form-input" value={formData.hotelName} onChange={e => handleInput('root', 'hotelName', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Property ID</label>
                  <input type="text" className="form-input" value={formData.propertyId} onChange={e => handleInput('root', 'propertyId', e.target.value)} placeholder="e.g. HCONN1" />
                </div>
              </div>
              <SyncConnectToggle
                value={formData.usesSyncConnect}
                onChange={v => handleInput('root', 'usesSyncConnect', v)}
              />
              <div className="form-group full-width">
                <label className="form-label required">Template</label>
                <div className={`template-cards-grid${mode === 'edit' ? ' template-cards-disabled' : ''}`}>
                  {templates.map(t => (
                    <div key={t.uuid} className={`template-card${formData.templateUuid === t.uuid ? ' selected' : ''}`}
                      onClick={() => mode !== 'edit' && handleInput('root', 'templateUuid', t.uuid)}>
                      <SyncTemplateGraphic template={t} compact />
                      <div className="template-card-body">
                        <div className="template-card-name">{t.name}</div>
                        {t.description && <div className="template-card-desc">{t.description}</div>}
                      </div>
                      {formData.templateUuid === t.uuid && <div className="template-card-check">✓</div>}
                    </div>
                  ))}
                </div>
              </div>
            </Card>
            <Card className="form-card">
              <h2 className="card-title">Project Details</h2>
              <div className="form-grid">
                <div className="form-group"><label className="form-label">Contact Name</label><input type="text" className="form-input" value={formData.projectDetails.projectContactName} onChange={e => handleInput('projectDetails', 'projectContactName', e.target.value)} /></div>
                <div className="form-group"><label className="form-label required">Cutover Date</label><input type="date" className="form-input" value={formData.projectDetails.projectCutoverDate} onChange={e => handleInput('projectDetails', 'projectCutoverDate', e.target.value)} required readOnly={!!booking} />{booking && <small>The committed booking's date.</small>}</div>
                <div className="form-group"><label className="form-label">Contact Email</label><input type="email" className="form-input" value={formData.projectDetails.projectContactEmail} onChange={e => handleInput('projectDetails', 'projectContactEmail', e.target.value)} /></div>
                <div className="form-group"><label className="form-label">Contact Phone</label><input type="tel" className="form-input" value={formData.projectDetails.projectContactPhone} onChange={e => handleInput('projectDetails', 'projectContactPhone', e.target.value)} /></div>
              </div>
            </Card>
            <div className="form-actions">
              <Button variant="secondary" type="button" onClick={() => navigate(syncId ? `/sync/${syncId}` : '/sync')}>Cancel</Button>
              <Button variant="primary" type="submit" disabled={saving}><Save className="icon" />{saving ? 'Saving...' : 'Save'}</Button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // ─── Wizard ────────────────────────────────────────────────────────────────
  if (mode === 'create' && partnerDataLoaded && !stripeReady) {
    return (
      <div className="sync-project-form-page">
        <div className="form-container">
          <div className="form-header">
            <div className="header-left">
              <Button variant="secondary" onClick={() => navigate('/sync')} className="back-button"><ArrowLeft className="icon" />Back</Button>
              <h1 className="form-title">Create Sync Project</h1>
            </div>
          </div>
          <div className="error-message">
            Your partner account must have Stripe connected before you can create Sync projects. Go to Partner Settings → Stripe to complete onboarding.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sync-project-form-page">
      <div className="form-container">
        <div className="form-header">
          <div className="header-left">
            <Button variant="secondary" onClick={() => navigate('/sync')} className="back-button"><ArrowLeft className="icon" />Back</Button>
            <h1 className="form-title">Create Sync Project</h1>
          </div>
        </div>

        {/* Step indicator */}
        <div className="vault-wizard-header">
          {STEPS.map((label, i) => {
            const isCompleted = i < step;
            const isCurrent = i === step;
            return (
              <React.Fragment key={i}>
                <div className={`wizard-step-item${isCurrent ? ' active' : ''}${isCompleted ? ' completed' : ''}`}>
                  <div className="wizard-step-circle">{isCompleted ? '✓' : i + 1}</div>
                  <div className="wizard-step-label">{label}</div>
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`wizard-step-connector${isCompleted ? ' completed' : ''}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Step 0: Choose Hotel — shared picker (self-fetching, scoped to the selected chain or partner) */}
        {step === 0 && (
          <WizardHotelStep
            title="Choose Hotel"
            subtitle="Select the hotel this sync project belongs to."
            accountId={selectedAccount}
            partnerId={selectedPartner}
            selectedHotel={selectedHotel}
            onSelect={setSelectedHotel}
          />
        )}

        {/* Step 1: Project Information */}
        {step === 1 && (
          <Card className="form-card">
            <h2 className="card-title">Project Information</h2>
            {(selectedHotel || hotelid) && (
              <div className="wizard-hotel-banner">
                Hotel: <strong>{selectedHotel?.hotelname || `#${hotelid}`}</strong>
              </div>
            )}
            <div className="form-grid">
              <div className="form-group full-width">
                <label className="form-label required">Project Name</label>
                <input type="text" className="form-input" value={formData.projectName}
                  onChange={e => handleInput('root', 'projectName', e.target.value)} required />
              </div>
              {/* Same control as the edit/copy form. Set here rather than only afterwards because it
                  decides how the property connects, which is part of deciding what the project IS. */}
              <SyncConnectToggle
                value={formData.usesSyncConnect}
                onChange={v => handleInput('root', 'usesSyncConnect', v)}
              />
              <div className="form-group full-width">
                <label className="form-label required">Template</label>
                {loading ? <p className="form-hint">Loading templates…</p> : (
                  <div className="template-cards-grid">
                    {templates.map(t => (
                      <div key={t.uuid} className={`template-card${formData.templateUuid === t.uuid ? ' selected' : ''}`}
                        onClick={() => handleInput('root', 'templateUuid', t.uuid)}>
                        <SyncTemplateGraphic template={t} compact />
                        <div className="template-card-body">
                          <div className="template-card-name">{t.name}</div>
                          {t.description && <div className="template-card-desc">{t.description}</div>}
                          {resolutions[t.id]?.level && (
                            <div className="resolved-source-inline">
                              {resolutions[t.id].level === 'hotel'    && <span className="resolved-badge resolved-hotel">Customised for this hotel — {resolutions[t.id].name}</span>}
                              {resolutions[t.id].level === 'account'  && <span className="resolved-badge resolved-account">Customised for this chain — {resolutions[t.id].name}</span>}
                              {resolutions[t.id].level === 'default'  && <span className="resolved-badge resolved-default">Partner default — {resolutions[t.id].name}</span>}
                              {resolutions[t.id].level === 'platform' && <span className="resolved-badge resolved-platform">Platform default tasks</span>}
                            </div>
                          )}
                        </div>
                        {formData.templateUuid === t.uuid && <div className="template-card-check">✓</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Step 2: Project Details */}
        {step === 2 && (
          <Card className="form-card">
            <h2 className="card-title">Project Details</h2>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Contact Name</label>
                <input type="text" className="form-input" value={formData.projectDetails.projectContactName}
                  onChange={e => handleInput('projectDetails', 'projectContactName', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label required">Cutover Date</label>
                <input type="date" className="form-input" value={formData.projectDetails.projectCutoverDate}
                  onChange={e => handleInput('projectDetails', 'projectCutoverDate', e.target.value)} required readOnly={!!booking} />
                {booking && <small>The date the partner committed to on Scheduling — it cannot be changed here.</small>}
              </div>
              <div className="form-group">
                <label className="form-label">Contact Email</label>
                <input type="email" className="form-input" value={formData.projectDetails.projectContactEmail}
                  onChange={e => handleInput('projectDetails', 'projectContactEmail', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Contact Phone</label>
                <input type="tel" className="form-input" value={formData.projectDetails.projectContactPhone}
                  onChange={e => handleInput('projectDetails', 'projectContactPhone', e.target.value)} />
              </div>
            </div>
          </Card>
        )}

        {/* Step 3: Conversions — optionally copy from another hotel in the same chain */}
        {step === 3 && (
          <Card className="form-card">
            <h2 className="card-title">Conversions</h2>
            <p className="form-hint" style={{ marginBottom: '1rem' }}>
              Copy conversions from another hotel in this chain, or skip to configure them after the project is created.
            </p>

            {conversionSource ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem', background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: '8px' }}>
                <span style={{ fontSize: '0.875rem', color: '#065f46' }}>
                  Conversions will be copied from <strong>{conversionSource.projectName}</strong>
                </span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="button" onClick={() => setShowCopyConversionsModal(true)} style={{ fontSize: '0.8rem', color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Change</button>
                  <button type="button" onClick={() => setConversionSource(null)} style={{ fontSize: '0.8rem', color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer' }}>✕ Clear</button>
                </div>
              </div>
            ) : (
              <Button variant="secondary" type="button" onClick={() => setShowCopyConversionsModal(true)}>
                <Copy className="icon-sm" /> Copy from another project
              </Button>
            )}
          </Card>
        )}

        {showCopyConversionsModal && (
          <CopyConversionsModal
            accountId={selectedHotel?.accountid}
            currentSyncUuid={null}
            templateName={templates.find(t => t.uuid === formData.templateUuid)?.name}
            onConfirm={handleConversionSourceSelected}
            onClose={() => setShowCopyConversionsModal(false)}
          />
        )}

        {/* Step 4: Notifications — who receives error alerts and run summaries */}
        {step === 4 && (
          <Card className="form-card">
            <h2 className="card-title">Notifications</h2>
            <p className="form-hint" style={{ marginBottom: '1rem' }}>
              Choose who receives error alerts and a summary of this project's sync activity.
            </p>
            <NotificationsPanel
              value={notif}
              onChange={setNotif}
              hotelId={effectiveHotelId}
              accountId={selectedHotel?.accountid || selectedAccount}
            />
          </Card>
        )}

        {/* Step 5: Payment — landing screen mirrors the vault payment step */}
        {step === 5 && hotelForPayment && (
          <WizardPaymentStep
            subtitle="A payment is required to proceed with sync project setup."
            description="A payment is required to proceed with sync project setup."
            awaiting={awaitingPayment}
            invoiceResult={invoiceResult}
            awaitingMessage="Your invoice has been emailed. The sync project is paused and not accessible until Stripe confirms the payment. The project's setup is saved — you can close this tab and come back any time."
            saving={saving}
            error={error}
            hotelId={hotelForPayment.hotelid}
            accountId={hotelForPayment.accountid || selectedAccount}
            invoicePrivilege={invoicePrivilege}
            currentUser={user}
            isSuperAdmin={isSuperAdmin}
            paymentService={buildSyncPaymentService(hotelForPayment.hotelid, hotelForPayment.accountid || selectedAccount)}
            onSuccess={handlePaymentComplete}
            onMarkPaid={handleMarkPaid}
            markPaidMessage="Mark this sync project as paid manually? This bypasses Stripe and immediately activates it."
          />
        )}

        {/* Navigation */}
        <div className="form-actions">
          {step > 0 && (
            <Button variant="secondary" type="button" onClick={back} disabled={saving}>
              <ArrowLeft className="icon" /> Back
            </Button>
          )}
          {step < STEPS.length - 1 && (
            <Button variant="primary" type="button" onClick={next} disabled={!canAdvance()}>
              Next <ArrowRight className="icon" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SyncProjectFormPage;
